import {
    ArrowRight,
    Calendar,
    Clock,
    Eye,
    EyeOff,
    Library,
    Lock,
    MapPin,
    ShieldCheck,
    Star,
    User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth.jsx';
import calendarioService from '../services/calendarioService';
import './PortalSelector.css';

const PortalSelector = () => {
    const [activePortalId, setActivePortalId] = useState('academia'); // Default to academia for maroon theme
    const [isLoginVisible, setIsLoginVisible] = useState(true); // Login form is now visible directly
    const [selectedRole, setSelectedRole] = useState('apoderado');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { login } = useAuth();
    const { changePrimaryColor } = useTheme();
    const navigate = useNavigate();

    // State for upcoming events (Mural)
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);


    const formatEventDate = (event) => {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        if (!event.fecha) return { day: '??', month: '???' };

        const date = new Date(event.fecha + 'T12:00:00');
        const day = String(date.getDate()).padStart(2, '0');
        const month = months[date.getMonth()];

        let endDay = null;
        let endMonth = null;
        if (event.fecha_fin && event.fecha_fin !== event.fecha) {
            const endDate = new Date(event.fecha_fin + 'T12:00:00');
            endDay = String(endDate.getDate()).padStart(2, '0');
            endMonth = months[endDate.getMonth()];
        }

        return { day, month, endDay, endMonth };
    };

    const getIcon = (type) => {
        switch (type) {
            case 'celebration': return <Star size={14} />;
            case 'exam': return <Library size={14} />;
            case 'meeting': return <User size={14} />;
            default: return <Calendar size={14} />;
        }
    };

    const getTypeStyle = (type) => {
        switch (type) {
            case 'celebration': return 'event-type-celebration';
            case 'exam': return 'event-type-exam';
            case 'holiday': return 'event-type-holiday';
            case 'meeting': return 'event-type-meeting';
            default: return 'event-type-default';
        }
    };

    const getFullImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const backendUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';
        return `${backendUrl}${url}`;
    };

    // Load remembered user and events
    useEffect(() => {
        const savedUser = localStorage.getItem('rememberedUser');
        if (savedUser) {
            setEmail(savedUser);
            setRememberMe(true);
        }

        const fetchEvents = async () => {
            try {
                const events = await calendarioService.obtenerProximosEventosPublicos();
                setUpcomingEvents(Array.isArray(events) ? events.slice(0, 3) : []);
            } catch (err) {
                console.error("No se pudieron cargar los eventos del mural:", err);
            } finally {
                setIsLoadingEvents(false);
            }
        };
        fetchEvents();
    }, []);

    // Unified Login Handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await login(email, password, 'PUBLIC');

            if (result.success) {
                if (rememberMe) {
                    localStorage.setItem('rememberedUser', email);
                } else {
                    localStorage.removeItem('rememberedUser');
                }

                // Force full reload to ensure scripts/styles initialize correctly
                window.location.href = '/';
            } else {
                setError(result.error || 'Credenciales inválidas');
            }
        } catch (err) {
            setError('Error al conectar con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    const groupedEvents = upcomingEvents.reduce((acc, event) => {
        const { month } = formatEventDate(event);
        if (!acc[month]) acc[month] = [];
        acc[month].push(event);
        return acc;
    }, {});

    return (
        <div className="portal-selector-wrapper">
            <div className="portal-background">
                <img src="/images/fondo_orionista.jpg" alt="Fondo Institucional" />
                <div className="portal-background-overlay"></div>
                <div className="portal-background-overlay-2"></div>
            </div>

            <main className="portal-main">
                <div className="portal-layout-container">
                    <div className="portal-login-card">
                        <div className="portal-inner-login">
                            <div className="portal-login-header-branded">
                                <img src="/icons/logo_orion.svg" alt="Orión Internacional" className="portal-card-internal-logo" />

                                <div className="aula-badge-container">
                                    <div className="aula-badge">
                                        <ShieldCheck size={14} style={{ color: '#818cf8' }} />
                                        Aula Virtual
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="portal-error-glass">
                                    <ShieldCheck size={18} className="flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="portal-login-form">

                                <div className="portal-form-group">
                                    <label className="portal-form-label">Correo Electrónico / Usuario</label>
                                    <div className="portal-input-wrapper">
                                        <User size={18} className="portal-input-icon" />
                                        <input
                                            type="text"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Ingrese su correo o usuario"
                                            className="portal-input"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="portal-form-group">
                                    <div className="portal-label-row">
                                        <label className="portal-form-label">Contraseña</label>
                                        <button type="button" className="portal-link-text">¿Olvidaste tu clave?</button>
                                    </div>
                                    <div className="portal-input-wrapper">
                                        <Lock size={18} className="portal-input-icon" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="********"
                                            className="portal-input"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="portal-password-toggle"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="portal-form-options">
                                    <label className="portal-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                        />
                                        <span className="checkbox-text">Mantener sesión iniciada</span>
                                    </label>
                                </div>

                                <button type="submit" className="portal-submit-button" disabled={isLoading}>
                                    {isLoading ? 'Iniciando sesión...' : (
                                        <>
                                            Ingresar al sistema
                                            <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="portal-card-footer">
                                <div className="footer-divider"></div>
                                <button type="button" className="portal-help-link">
                                    <ShieldCheck size={16} />
                                    ¿Necesitas ayuda con tu acceso?
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: EVENT MURAL */}
                    {Object.keys(groupedEvents).length > 0 && (
                        <div className="portal-event-feed">
                            <div className="portal-feed-main-header">
                                <span className="feed-badge">COMUNICADOS</span>
                                <h1 className="feed-title">Próximos Eventos</h1>
                                <div className="feed-header-line" />
                            </div>

                            {Object.entries(groupedEvents).map(([month, events]) => (
                                <div key={month} className="login-event-month-section">
                                    <div className="login-month-header">
                                        <div className="login-month-icon">
                                            <Calendar size={18} />
                                        </div>
                                        <h2 className="login-month-name">{month}</h2>
                                        <div className="login-month-divider" />
                                    </div>

                                    <div className="portal-events-grid">
                                        {events.map((event) => {
                                            const { day, endDay, endMonth } = formatEventDate(event);
                                            const isFeatured = event.es_destacado;

                                            return (
                                                <div key={event.id} className={`event-card login-event-card ${isFeatured ? 'featured' : ''} ${getTypeStyle(event.tipo_evento)}`}>
                                                    {event.imagen_url && (
                                                        <>
                                                            <div className="event-card-bg">
                                                                <img src={getFullImageUrl(event.imagen_url)} alt={event.titulo} />
                                                            </div>
                                                            <div className="event-card-overlay" />
                                                        </>
                                                    )}

                                                    <div className="event-card-content">
                                                        <div className="event-card-top">
                                                            <div className="event-type-pills">
                                                                <div className={`event-type-badge ${getTypeStyle(event.tipo_evento)}`}>
                                                                    {event.tipo_evento === 'celebration' ? 'Actividad' :
                                                                        event.tipo_evento === 'exam' ? 'Examen' :
                                                                            event.tipo_evento === 'holiday' ? 'Feriado' :
                                                                                event.tipo_evento === 'meeting' ? 'Reunión' :
                                                                                    event.tipo_evento === 'birthday' ? 'Cumpleaños' :
                                                                                        event.tipo_evento === 'evaluation' ? 'Eval. Mensual' :
                                                                                            'Hito'}
                                                                </div>
                                                            </div>
                                                            <div className="event-date-box">
                                                                <span className="event-date-month">{month.substring(0, 3).toUpperCase()}</span>
                                                                <span className="event-date-day">
                                                                    {day}
                                                                    {endDay && endDay !== day && (
                                                                        <span className="date-range-sep"><br /> - {endMonth !== month ? `${endDay} ${endMonth.substring(0, 3)}` : endDay}</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="event-card-bottom">
                                                            <h3 className="event-card-title">{event.titulo}</h3>
                                                            <div className="event-card-meta-container">
                                                                <div className="event-card-meta">
                                                                    {getIcon(event.tipo_evento)}
                                                                    <span>{event.descripcion}</span>
                                                                </div>
                                                                {(event.hora_inicio || event.ubicacion) && (
                                                                    <div className="event-card-details">
                                                                        {event.hora_inicio && (
                                                                            <div className="detail-item">
                                                                                <Clock size={12} />
                                                                                <span>{event.hora_inicio.substring(0, 5)}</span>
                                                                            </div>
                                                                        )}
                                                                        {event.ubicacion && (
                                                                            <div className="detail-item">
                                                                                <MapPin size={12} />
                                                                                <span>{event.ubicacion}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main >
        </div >
    );
};

export default PortalSelector;
